import {
  Component,
  computed,
  inject,
  Injector,
  linkedSignal,
  runInInjectionContext,
  signal,
  WritableSignal,
  OnInit,
  ViewContainerRef,
} from '@angular/core';

import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { ApiService, qrCodeInfo } from '@cue/api';
import { Store } from '@ngrx/store';
import { allResources, AppState, LocaleService } from '@assist/shared/data';
import { NgClass } from '@angular/common';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { map, of } from 'rxjs';
import {
  cueInfo,
  IconComponent,
  cueTablet,
  cueUser,
  cueSpeaker,
  cueNavigationCircle,
  cueNews,
  cueCalendarCheck,
  cueOrders,
  cueClose,
  cueArrowDown,
} from '@cue/ui/icons';
import { MultiviewDetailComponent } from '../multiview-detail/multiview-detail.component';
import { LoadingComponent } from '@cue/loading';
import { LocalizedStringPipe } from '@cue/utilities';
import { MultiviewEventsComponent } from '../multiview-events/multiview-events.component';
import { MultiviewRequestsComponent } from '../multiview-requests/multiview-requests.component';
import { MultiviewNewsComponent } from '../multiview-news/multiview-news.component';
import { NewsService } from '@assist/news/data';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { MultiviewMoreComponent } from '../multiview-more/multiview-more.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { NavigationDetailInfo, NavigationService } from '@cue/navigation-services';
import { tap } from 'rxjs/operators';
import { MultiviewDeviceComponent } from '../multiview-device/multiview-device.component';
import { MultiViewControl, MultiviewControlComponent } from '../multiview-control/multiview-control.component';
import { MultiviewNavigationComponent } from '../multiview-navigation/multiview-navigation.component';
import { MultiviewAnonymousDetailComponent } from '../multiview-anonymous-detail/multiview-anonymous-detail.component';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { PoisCanBeShownInResourcePipe } from '@cue/navigation-widgets';
import {
  DropdownlistComponent,
  DropdownsItemTemplateDirective,
  ValueTemplateDirective,
} from '@cue/assist/ui/dropdowns';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

export type MultiViewData = {
  mode: 'anonymous' | 'authenticated';
};

type BarItemType = 'detail' | 'navigation' | 'events' | 'requests' | 'news' | 'control' | 'device' | 'more';

// nema QR licenci
//  http://localhost:4201/dashboard?multiview=cb45621d-8f7e-4b9a-a602-1ee0b88508dc

// resource nema licenci, QR ano
//  http://localhost:4201/dashboard?multiview=c0247f5f-d54b-4eb7-a1fc-525306513972

// DASSIST-prostredi url
// NEWS - yoga
//   http://localhost:4201/dashboard?multiview=8d0d298d-22d4-4fd9-9492-1efe137718c2

// Nejaky QR bez resourcu - dako
//  http://localhost:4201/dashboard?multiview=8c5ea695-0266-4ec3-a3f8-0d4e1ddf9560

// RESOURCE - hefaistos
//  http://localhost:4201/dashboard?multiview=6cfa8112-9fe1-4b22-bac2-9fe6c751ebcd

// Hefaistos - nejaky QR code, nemam tuseni
//   http://localhost:4201/dashboard?multiview=0bcd7466-1c5a-4160-947a-d7ec0a5deea3
@Component({
  selector: 'assist-multiview',
  imports: [
    IconComponent,
    NgClass,
    MultiviewDetailComponent,
    LoadingComponent,
    MultiviewEventsComponent,
    MultiviewRequestsComponent,
    MultiviewNewsComponent,
    MultiviewMoreComponent,
    TranslocoPipe,
    MultiviewDeviceComponent,
    MultiviewControlComponent,
    MultiviewNavigationComponent,
    MultiviewAnonymousDetailComponent,
    DropdownlistComponent,
    DropdownsItemTemplateDirective,
    ValueTemplateDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './multiview.component.html',
  styleUrl: './multiview.component.scss',
  providers: [LocalizedStringPipe, PoisCanBeShownInResourcePipe],
})
export class MultiviewComponent implements OnInit {
  ICONS = {
    cueArrowDown,
    cueInfo,
    cueNavigationCircle,
    cueCalendarCheck,
    cueOrders,
    cueNews,
    cueTablet,
    cueSpeaker,
    cueClose,
    cueUser,
  };

  vcf = inject(ViewContainerRef);
  qrCodeId = injectQueryParams('multiview');
  protected localeService = inject(LocaleService);
  dialogData = inject(DIALOG_DATA) as MultiViewData;
  currentLanguageControl = new FormControl<{ text: string; flag: string }>(null);
  apiService = inject(ApiService);

  dialogRef = inject(DialogRef);

  navigationService = inject(NavigationService);

  poisCanBeShownInResourcePipe = inject(PoisCanBeShownInResourcePipe);

  newsService = inject(NewsService);
  #localizedStringPipe = inject(LocalizedStringPipe);
  #store = inject(Store<AppState>);
  #allResources = toSignal(this.#store.select(allResources));

  availableBarItems = computed<BarItemType[]>(() => {
    const items: BarItemType[] = [];
    const value = this.preflightRequest.hasValue() ? this.preflightRequest.value() : null;
    if (!value) {
      return [];
    }

    if ((this.isResourceQR() && this.resource()) || (this.isAnonymousResourceQR() && this.resource())) {
      items.push('detail');
    } else if (this.isAnonymousResourceQR() && this.anonymousResource.hasValue()) {
      items.push('detail');
    }

    if (this.isNewsQR()) {
      items.push('news');
    }

    if (this.isResourceQR() && this.resource()) {
      items.push('events');
    }

    if (this.isResourceQR() && this.hasServicesAvailable.hasValue() && this.hasServicesAvailable.value()) {
      items.push('requests');
    }
    if (this.controls()?.length > 0) {
      items.push('control');
    }

    if (this.devices()?.length > 0) {
      items.push('device');
    }

    if (this.hasLocation()) {
      items.push('navigation');
    }

    if (this.dialogData.mode === 'anonymous') {
      items.push('more');
    }
    return items;
  });

  ngOnInit() {
    this.currentLanguageControl.setValue(this.localeService.currentLanguage, {
      emitEvent: false,
    });
    this.currentLanguageControl.valueChanges.subscribe((val) => {
      this.localeService.currentLanguage = val;
    });
  }

  activeBarItem = linkedSignal<BarItemType | null>(() => {
    const items = this.availableBarItems();
    const preferedItem = this.queryParamTab();
    if (preferedItem && items.includes(preferedItem as BarItemType)) {
      return preferedItem as BarItemType;
    }
    return items.length > 0 ? items[0] : null;
  });

  queryParamTab = linkedQueryParam('tab', {
    queryParamsHandling: 'merge',
    replaceUrl: true,
  });

  injector = inject(Injector);

  qrDetails = signal<Record<string, WritableSignal<NavigationDetailInfo | null>>>({});

  devices = computed(() => {
    const devices: NavigationDetailInfo[] = [];
    Object.values(this.qrDetails()).forEach((detailSignal) => {
      if (detailSignal()?.unitInformation) {
        devices.push(detailSignal());
      }
    });
    return devices;
  });

  controls = computed<MultiViewControl[]>(() => {
    if (!this.serverDetail.hasValue()) return [];

    const result: MultiViewControl[] = [];

    const poisCanBeShownInResource = this.poisCanBeShownInResourcePipe.transform(this.serverDetail.value()?.qrCodes);
    const filteredQrDetails = Object.entries(this.qrDetails()).filter(([, detailSignal]) => {
      const detail = detailSignal();
      return poisCanBeShownInResource.some((qr) => qr.qrCodeId === detail?.qrCodeId);
    });

    filteredQrDetails.forEach(([id, detailSignal]) => {
      console.log(id);
      if (detailSignal()?.objects?.length > 0) {
        const toPush: MultiViewControl = {
          poi: detailSignal(),
          objects: detailSignal()?.objects,
          qrCodeId: detailSignal()?.qrCodeId,
          qrCodeListOfResource: poisCanBeShownInResource.map((qr) => qr.qrCodeId),
        };
        result.push(toPush);
      }
    });

    return result;
  });

  unitInfos = computed(() => {
    return this.devices().map((device) => device.unitInformation);
  });

  fetchQrDetail(id: string) {
    if (!this.qrDetails()[id]) {
      runInInjectionContext(this.injector, () => {
        this.qrDetails.update((s) => ({
          ...s,
          [id]: signal<NavigationDetailInfo | null>(null),
        }));
      });

      this.navigationService
        .getNavigationDetailForMultiview({
          qrCodeId: id,
          kioskId: null,
          appId: null,
        })
        .pipe(
          map((r) => r ?? null),
          tap((r) =>
            this.qrDetails.update((s) => ({
              ...s,
              [id]: signal<NavigationDetailInfo | null>(r ? r : null),
            }))
          )
        )
        .subscribe();
    }
  }

  serverDetail = rxResource({
    params: () => this.resource()?.idForFiltering,
    stream: ({ params }) => {
      return this.navigationService
        .getNavigationDetailForMultiview({
          resourceId: params ? params : undefined,
          qrCodeId: params ? undefined : this.qrCodeId(),
          kioskId: null,
          appId: null,
        })
        .pipe(map((x) => x ?? null));
    },
  });

  qrCodes = computed(() => {
    if (this.serverDetail.isLoading()) {
      return [];
    }

    if (!this.serverDetail.isLoading() && !this.serverDetail.hasValue()) {
      return [this.qrCodeId()];
    }

    return this.serverDetail.value()?.qrCodes.map((qr) => qr.qrCodeId) || [];
  });

  loadQrCodeDetails = explicitEffect([this.qrCodes], ([qrCodes]) => {
    qrCodes.forEach((qr) => {
      if (qr) {
        this.fetchQrDetail(qr);
      }
    });
  });

  news = rxResource({
    params: () => (this.qrCodeInfo.hasValue() ? this.qrCodeInfo.value()?.data?.newsId : undefined),
    stream: ({ params }) => {
      if (!params) {
        return of(null);
      }
      return this.newsService.getNewsById(params);
    },
  });

  setActiveBarItem(item: BarItemType) {
    this.queryParamTab.set(item);
  }

  preflightRequest = rxResource({
    params: () => this.qrCodeId(),
    stream: ({ params }) => this.apiService.call(qrCodeInfo(params, null, false)),
  });

  qrCodeInfo = rxResource({
    params: () => this.qrCodeId(),
    stream: ({ params }) => this.apiService.call(qrCodeInfo(params, null, true)),
  });

  hasLocation = computed(() => {
    return this.qrCodeInfo.hasValue() ? this.qrCodeInfo.value().data.hasLocation : false;
  });

  isAnonymous = computed(() => {
    return this.dialogData.mode === 'anonymous';
  });

  // Resourcy
  isResourceQR = computed(() => {
    return (
      this.dialogData.mode !== 'anonymous' &&
      this.preflightRequest.hasValue() &&
      !this.preflightRequest.value().data.resourceNotAvailable &&
      (this.preflightRequest.value().data.resourceId || this.preflightRequest.value().data.resourceGuid !== null)
    );
  });

  isAnonymousResourceQR = computed(() => {
    return (
      this.dialogData.mode === 'anonymous' &&
      this.anonymousResource.hasValue() &&
      this.anonymousResource.value() != null
    );
  });
  anonymousResource = rxResource({
    params: () => {
      const params = {
        anonymous: this.dialogData.mode === 'anonymous',
        qrCodeInfo: this.qrCodeInfo.hasValue() ? this.qrCodeInfo.value() : undefined,
      };
      if (!params.anonymous || !params.qrCodeInfo || !params.qrCodeInfo.data?.resourceId) {
        return undefined;
      }
      return params;
    },
    stream: ({ params }) => {
      return this.navigationService.getNavigationDetailForMultiview({
        resourceId: params.qrCodeInfo.data.resourceId,
        kioskId: null,
        appId: null,
      });
    },
  });

  resource = computed(() => {
    if (this.dialogData.mode === 'anonymous') {
      return null;
    }

    if (!this.isResourceQR()) {
      return null;
    }

    if (!this.preflightRequest.hasValue()) return null;

    const foundResource = this.#allResources().data.find(
      (resource) =>
        resource.idForFiltering === this.preflightRequest.value().data.resourceId ||
        resource.id === this.preflightRequest.value().data.resourceGuid
    );

    if (foundResource) {
      return foundResource;
    }

    return null;
  });

  hasServicesAvailable = rxResource({
    params: () => this.resource()?.id,
    stream: ({ params }) => {
      if (!params) return of(null);
      return this.apiService
        .call<
          any,
          Record<
            string,
            {
              id: string;
              enableForAssist: boolean;
              resourceRequestAllowed: boolean;
              eventRequestAllowed: boolean;
            }
          >
        >({
          url: '/api/assist/resources/services/availability',
          method: 'post',
          payload: { guids: [params] },
        })
        .pipe(map((x) => x[params].enableForAssist && x[params].resourceRequestAllowed));
    },
  });

  translocoService = inject(TranslocoService);

  // News
  isNewsQR = computed(() => {
    return this.qrCodeInfo.hasValue() && this.qrCodeInfo.value().data.newsId !== null;
  });

  loading = computed(() => {
    return this.qrCodeInfo.isLoading() || this.hasServicesAvailable.isLoading();
  });

  error = computed(() => {
    return this.qrCodeInfo.error();
  });

  title = computed(() => {
    if (this.isResourceQR()) {
      if (this.resource()) {
        const displayNameApp = this.#localizedStringPipe.transform(this.resource().displayNameForApp);
        if (displayNameApp) {
          return displayNameApp;
        }
        return this.#localizedStringPipe.transform(this.resource().displayName);
      }
    }

    if (this.isAnonymousResourceQR()) {
      if (this.anonymousResource.value()) {
        const displayNameApp = this.#localizedStringPipe.transform(this.anonymousResource.value().displayNameForApp);
        if (displayNameApp) {
          return displayNameApp;
        }
        return this.#localizedStringPipe.transform(this.anonymousResource.value()?.name);
      }
    }

    if (this.isNewsQR()) {
      if (this.news.value()) {
        return this.#localizedStringPipe.transform(this.news.value().title);
      }
    }

    if (Object.keys(this.qrDetails()).length > 0) {
      const value = this.qrDetails()[Object.keys(this.qrDetails())[0]]();
      if (value) {
        return this.#localizedStringPipe.transform(value.name);
      }
    }
    return 'POI';
  });

  areaId = computed(() => {
    if (this.resource()) {
      return this.resource().mapInfos[0].areaId;
    }

    return this.qrCodeInfo.value()?.data?.startAreaId ?? null;
  });

  protected readonly translation = translation;
}
